// In-memory StoreBackend (ARCHITECTURE.md §12, fallback path).
//
// Used when better-sqlite3 is not installed. Replicates the row semantics of
// the SQLite schema (§12.3) using plain Maps so that the writer/reader functions
// are backend-agnostic. Data does not persist across process invocations —
// callers that need persistence must install the better-sqlite3 optional peer
// dependency.

import type { FileRow, StoreBackend } from "./types.js";

interface Snapshot {
  metadata: Map<string, string>;
  files: Map<number, FileRow>;
  pathIndex: Map<string, number>;
  counters: { file: number };
}

const SCHEMA_VERSION = 1;

export function createMemoryBackend(): StoreBackend {
  let metadata = new Map<string, string>();
  let files = new Map<number, FileRow>();
  let pathIndex = new Map<string, number>();
  const counters = { file: 0 };

  function cascadeDeleteFile(fileId: number): void {
    files.delete(fileId);
  }

  function snapshot(): Snapshot {
    return {
      metadata: new Map(metadata),
      files: new Map(files),
      pathIndex: new Map(pathIndex),
      counters: { ...counters },
    };
  }

  function restore(snap: Snapshot): void {
    metadata = snap.metadata;
    files = snap.files;
    pathIndex = snap.pathIndex;
    counters.file = snap.counters.file;
  }

  return {
    getSchemaVersion() {
      return SCHEMA_VERSION;
    },

    getMetadata(key) {
      return metadata.get(key);
    },
    setMetadata(key, value) {
      metadata.set(key, value);
    },

    insertOrReplaceFile(file: Omit<FileRow, "id">): number {
      const existing = pathIndex.get(file.path);
      if (existing !== undefined) {
        cascadeDeleteFile(existing);
        pathIndex.delete(file.path);
      }
      const id = ++counters.file;
      files.set(id, { id, ...file });
      pathIndex.set(file.path, id);
      return id;
    },
    deleteFileByPath(path: string): boolean {
      const id = pathIndex.get(path);
      if (id === undefined) return false;
      cascadeDeleteFile(id);
      pathIndex.delete(path);
      return true;
    },
    getFileByPath(path: string): FileRow | undefined {
      const id = pathIndex.get(path);
      return id === undefined ? undefined : files.get(id);
    },
    getFileById(id: number): FileRow | undefined {
      return files.get(id);
    },
    getAllFiles(): FileRow[] {
      return [...files.values()];
    },

    transaction<T>(fn: () => T): T {
      const snap = snapshot();
      try {
        return fn();
      } catch (err) {
        restore(snap);
        throw err;
      }
    },

    close(): void {
      metadata.clear();
      files.clear();
      pathIndex.clear();
    },
  };
}
